import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Avatar, Stack, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { InspectionIcon } from 'app/icons';
import { ChecklistTemplateForm, ChecklistTemplateHistoryList } from 'app/pages/Checklist';
import ChecklistTemplateAutocolonList from 'app/pages/Checklist/ChecklistTemplateAutocolonList';
import { useAsyncAction } from 'app/services';
import { templateDataLoad, templateUpdate } from 'app/store/checklist';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const ChecklistTemplateView = () => {
  const formRef = useRef();
  const { templateSelectedItem } = useSelector((state) => state.checklist);
  const [tab, setTab] = useState('info');

  const { asyncAction: fetchData, isLoading: isFetchDataLoading } = useAsyncAction(templateDataLoad, [templateSelectedItem]);

  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(templateUpdate, [templateSelectedItem]);

  useEffect(() => {
    if (!templateSelectedItem) return;

    switch (tab) {
      case 'history':
        !isFetchDataLoading && fetchData(templateSelectedItem.id);
        break;
    }
  }, [tab, templateSelectedItem]);

  return (
    <Stack
      direction='column'
      alignItems='center'
      sx={{
        height: '100%',
        flex: '1 1 auto',
        '.simplebar-content': {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}>
      {templateSelectedItem && (
        <Stack
          direction='column'
          alignItems='center'
          sx={(theme) => ({
            maxWidth: '540px',
            width: '100%',
            height: tab === 'history' ? '100%' : 'auto',
            padding: theme.spacing(2),
          })}>
          <Stack
            direction='column'
            alignItems='center'
            sx={(theme) => ({
              marginBottom: theme.spacing(3),
              '.MuiAvatar-root': {
                width: '56px',
                height: '56px',
                bgcolor: theme.palette.primary.main,
              },
              '.MuiAvatar-circular': {
                color: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                svg: {
                  width: '30px',
                  height: '30px',
                },
                path: {
                  fill: theme.palette.primary.main,
                },
              },
              '.MuiTypography-root:first-of-type': {
                marginTop: theme.spacing(1),
              },
            })}>
            <Avatar>
              <InspectionIcon fontSize='large' />
            </Avatar>
            <Typography variant='body1'>{templateSelectedItem.name}</Typography>
          </Stack>
          <Stack
            direction='column'
            alignItems='center'
            sx={(theme) => ({
              width: '100%',
              height: tab === 'history' ? '100%' : 'auto',
              position: 'relative',
              border: '1px solid',
              borderRadius: theme.shape.borderRadius,
              borderColor: theme.palette.divider,
              bgcolor: 'background.paper',
              '.MuiTabPanel-root': {
                width: '100%',
                padding: theme.spacing(2),
              },
            })}>
            <TabContext value={tab}>
              <TabList
                onChange={(event, value) => setTab(value)}
                TabIndicatorProps={{ children: <span /> }}
                sx={(theme) => ({
                  width: '100%',
                  minHeight: '44px',
                  borderBottom: '1px solid',
                  borderColor: theme.palette.divider,
                  '.MuiTab-root': {
                    fontSize: '12px',
                    minHeight: '44px',
                    padding: theme.spacing(2),
                    '&.Mui-selected': {
                      color: 'inherit',
                    },
                  },
                  '.MuiTabs-indicator': {
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                  },
                  '.MuiTabs-indicator span': {
                    width: 'calc(100% - ' + theme.spacing(4) + ')',
                    bgcolor: theme.palette.primary.main,
                    borderRadius: theme.shape.borderRadius,
                  },
                })}>
                <Tab value='info' label='Информация' disableRipple />
                <Tab value='history' label='История изменений' disableRipple />
                <Tab value='autocolon' label={config.clientUi === 'voykan' ? 'Направление осмотров' : 'Автоколонны'} disableRipple />
              </TabList>
              <TabPanel value='info'>
                <ChecklistTemplateForm ref={formRef} defaultValues={templateSelectedItem} onSubmit={(params) => updateSubmit(params)} />
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {!isUpdateSuccessful && isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {isUpdateSuccessful && !isUpdateError && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(3),
                  })}>
                  <LoadingButton
                    variant='contained'
                    loading={isUpdateLoading}
                    onClick={() => formRef.current?.handleFormSubmit()}
                    disableElevation>
                    Сохранить изменения
                  </LoadingButton>
                </Stack>
              </TabPanel>
              <TabPanel value='history' style={{ height: '100%', padding: '0' }}>
                <ChecklistTemplateHistoryList />
              </TabPanel>
              <TabPanel value='autocolon' style={{ height: '100%', padding: '0' }}>
                <ChecklistTemplateAutocolonList />
              </TabPanel>
            </TabContext>
          </Stack>
        </Stack>
      )}
      {!templateSelectedItem && (
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          sx={(theme) => ({
            height: '100%',
            padding: theme.spacing(0, 2),
            '.MuiTypography-root': {
              borderRadius: '20px',
              marginTop: '24px',
              padding: theme.spacing(0.5, 2),
              bgcolor: alpha(theme.palette.grey.main, 0.12),
            },
          })}>
          <Typography align='center' color='text.secondary' variant='caption' component='label'>
            Выберите шаблон из списка
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

export default ChecklistTemplateView;
